import {
  type ResponseGetMerchflow_merchflowId_StorePogTriggerAllFinished,
  RouteGetMerchflow_merchflowId_StorePogTriggerAllFinished,
} from "@CommonApi/merchflow/_merchflowId_/store-pog-trigger/all-finished";
import type { ResponseGetMerchflow_merchflowId_FlowUpdateChangeSummary } from "@CommonApi/merchflow/_merchflowId_/flow-update/change-summary";
import {
  type ResponseGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_,
  RouteGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_,
} from "@CommonApi/merchflows/_merchflowId_/flow-update/_storePogTriggerId_";
import { routeToUrl } from "@CommonUtils/routes";
import { useEffect, useState } from "react";
import { useApi } from "../../api";
import { Page, PageStatus } from "../../components";
import { useToasts } from "../../components/Toasts";
import { useLoaders } from "../../components/InlineLoader";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import { Button, Color, Flex, Pagination, Select, Text } from "../../elements";
import { useAsyncState } from "../../hooks/useAsyncState";
import type { FlowUpdateStorePogTriggerResponse } from "@CommonApi/merchflows/_merchflowId_/flow-update/_storePogTriggerId_";
import { downloadFile, pages } from "../../utils";
import { useNavigation } from "../../utils/navigation";
import { useParams } from "../../utils/useParams";
import {
  UpdateReportGroupedTable,
  type UpdateReportDetail,
} from "./components/UpdateReportGroupedTable";
import { isFlowUpdateStorePogTriggerError } from "src/utils/flowUpdate";
import { storePogVersionLabel } from "src/utils/storePogVersions";

const PAGE_SIZE = 20;

type FlowUpdateReport = ResponseGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_;
type FlowUpdateChangeSummary = ResponseGetMerchflow_merchflowId_FlowUpdateChangeSummary;

const isFacingsChangeDetail = (detail: UpdateReportDetail) =>
  detail.changeType === "FACINGS_INCREASED" || detail.changeType === "FACINGS_DECREASED";

const needsFacingsEnrichment = (detail: UpdateReportDetail) =>
  isFacingsChangeDetail(detail) &&
  (detail.oldFacings === undefined || detail.newFacings === undefined);

const buildEnrichedDetails = (
  details: UpdateReportDetail[],
  summariesByStoreCode: Record<string, FlowUpdateChangeSummary | null>,
): UpdateReportDetail[] => {
  const facingsByStoreCode: Record<
    string,
    Record<string, { oldFacings: number; newFacings: number }>
  > = {};

  for (const [storeCode, summary] of Object.entries(summariesByStoreCode)) {
    for (const entry of summary?.change_summary ?? []) {
      if (entry.change_type !== "update") {
        continue;
      }

      facingsByStoreCode[storeCode] ??= {};
      facingsByStoreCode[storeCode][entry.product_code] = {
        oldFacings: entry.old_facings,
        newFacings: entry.new_facings,
      };
    }
  }

  return details.map((detail) => {
    if (!needsFacingsEnrichment(detail)) {
      return detail;
    }

    const facings = facingsByStoreCode[detail.storeCode]?.[detail.productCode];

    if (!facings) {
      return detail;
    }

    return {
      ...detail,
      oldFacings: detail.oldFacings ?? facings.oldFacings,
      newFacings: detail.newFacings ?? facings.newFacings,
    };
  });
};

export const PageUpdateReport = () => {
  const { merchflowId, storePogTriggerId } = useParams<{
    merchflowId: number;
    storePogTriggerId: number;
  }>();

  const {
    getStorePropStatusApi,
    downloadFlowUpdateExportApi,
    getMerchflowStorePogTriggerAllFinishedApi,
    getMerchflowFlowUpdateChangeSummaryApi,
  } = useApi();
  const { hasLoader } = useLoaders();
  const { navigate } = useNavigation();
  const { toast } = useToasts();

  const [flowUpdateReport, setFlowUpdateReport] =
    useAsyncState<FlowUpdateStorePogTriggerResponse>();
  const [finishedStorePogTriggers, setFinishedStorePogTriggers] =
    useAsyncState<ResponseGetMerchflow_merchflowId_StorePogTriggerAllFinished>();
  const [resolvedDetails, setResolvedDetails] = useState<{
    report: FlowUpdateReport;
    details: UpdateReportDetail[];
  } | null>(null);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const isUpdateReportLoading =
    flowUpdateReport === undefined ||
    hasLoader(RouteGetMerchflows_merchflowId_FlowUpdate_storePogTriggerId_);
  const isFinishedStorePogTriggersLoading =
    finishedStorePogTriggers === undefined ||
    hasLoader(RouteGetMerchflow_merchflowId_StorePogTriggerAllFinished);
  const hasSuccessfulUpdateReport = Boolean(
    flowUpdateReport &&
    !isFlowUpdateStorePogTriggerError(flowUpdateReport) &&
    flowUpdateReport.details.length > 0,
  );

  const totalStores =
    flowUpdateReport && !isFlowUpdateStorePogTriggerError(flowUpdateReport)
      ? flowUpdateReport.total_stores
      : 0;
  const totalPages = Math.ceil(totalStores / PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [storePogTriggerId]);

  useEffect(() => {
    let cancelled = false;

    getStorePropStatusApi(merchflowId, storePogTriggerId, { page, pageSize: PAGE_SIZE }).then(
      (result) => {
        if (!cancelled) {
          setFlowUpdateReport(result);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [merchflowId, storePogTriggerId, page]);

  useEffect(() => {
    setFinishedStorePogTriggers(getMerchflowStorePogTriggerAllFinishedApi(merchflowId));
  }, [merchflowId]);

  useEffect(() => {
    let cancelled = false;

    if (!flowUpdateReport || isFlowUpdateStorePogTriggerError(flowUpdateReport)) {
      setResolvedDetails(null);
      return () => {
        cancelled = true;
      };
    }

    const report = flowUpdateReport;
    const reportDetails = report.details as UpdateReportDetail[];
    const storeCodesToEnrich = Array.from(
      new Set(
        reportDetails
          .filter(needsFacingsEnrichment)
          .map((detail) => detail.storeCode)
          .filter((storeCode) => {
            const pogIds = report.storePogIds?.[storeCode];
            return (
              pogIds !== undefined &&
              pogIds.previousStorePogId !== null &&
              pogIds.updatedStorePogId !== null
            );
          }),
      ),
    );

    if (storeCodesToEnrich.length === 0) {
      setResolvedDetails({ report, details: reportDetails });
      return () => {
        cancelled = true;
      };
    }

    const loadFacingsSummaries = async () => {
      const summaries = await Promise.all(
        storeCodesToEnrich.map(async (storeCode) => {
          const pogIds = report.storePogIds?.[storeCode];

          if (!pogIds || pogIds.previousStorePogId === null || pogIds.updatedStorePogId === null) {
            return [storeCode, null] as const;
          }

          const summary = await getMerchflowFlowUpdateChangeSummaryApi(merchflowId, {
            old_pog_id: pogIds.previousStorePogId,
            new_pog_id: pogIds.updatedStorePogId,
          }).catch(() => null);

          return [storeCode, summary] as const;
        }),
      );

      if (cancelled) {
        return;
      }

      setResolvedDetails({
        report,
        details: buildEnrichedDetails(reportDetails, Object.fromEntries(summaries)),
      });
    };

    void loadFacingsSummaries();

    return () => {
      cancelled = true;
    };
  }, [flowUpdateReport, merchflowId]);

  const buildStorePogUrl = (storeCode: string, storePogId: number) =>
    routeToUrl(pages.merchflows_merchflowId_Stores_storeCode_Pogs_storePogId_, {
      merchflowId,
      storeCode,
      storePogId,
    });

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} />

      <Flex
        justify="right"
        padding="10px"
        borderColor={Color.lightGrayHover}
        borderRadius="3px"
        gap="10px"
      >
        <Button
          color="greenSmoke"
          variant="inverted"
          onClick={() =>
            navigate(routeToUrl(pages.merchflows_merchflowId_StoresAll, { merchflowId }))
          }
          iconLeft={{ name: "arrowLeft" }}
        >
          Back to Flow
        </Button>
        <Button
          color="greenSmoke"
          isDisabled={!hasSuccessfulUpdateReport || isUpdateReportLoading || isExporting}
          isLoading={isExporting}
          iconLeft={{ name: "fileCsv" }}
          onClick={async () => {
            setIsExporting(true);
            try {
              const csvData = await downloadFlowUpdateExportApi(merchflowId, storePogTriggerId);
              downloadFile({
                name: `update_report_${merchflowId}_${storePogTriggerId}`,
                extension: "csv",
                data: csvData,
              });
            } catch {
              toast({
                type: "ERROR",
                title: "Export failed",
                message: "Could not download the update report CSV.",
              });
            } finally {
              setIsExporting(false);
            }
          }}
        >
          Export CSV
        </Button>
        <Select
          width="120px"
          title="Update ID"
          options={
            finishedStorePogTriggers
              ?.filter((storePogTrigger) => storePogTrigger.trigger_type === "FLOW_UPDATE")
              .map((storePogTrigger) => ({
                label: storePogVersionLabel(storePogTrigger),
                value: storePogTrigger.store_pog_trigger_id,
              })) || []
          }
          value={storePogTriggerId}
          setValue={(newId) => {
            navigate(
              routeToUrl(pages.merchflows_merchflowId_UpdateReport_storePogTriggerId, {
                merchflowId,
                storePogTriggerId: newId,
              }),
            );
          }}
          isLoading={isFinishedStorePogTriggersLoading}
          isDisabled={isUpdateReportLoading}
        />
      </Flex>

      {isUpdateReportLoading ? (
        <PageStatus label="Loading update report..." icon="spinner" />
      ) : flowUpdateReport === null ? (
        <PageStatus label="Failed to load update report..." icon="alert" color={Color.red} />
      ) : isFlowUpdateStorePogTriggerError(flowUpdateReport) ? (
        <Flex column align="center" padding="20px" gap="12px">
          <PageStatus label={flowUpdateReport.message} icon="alert" color={Color.red} />

          {flowUpdateReport.errors.length > 0 && (
            <Flex column align="center" gap="4px">
              {flowUpdateReport.errors.map((error, index) => (
                <Text key={`${error}-${index}`} color={Color.red} textAlign="center">
                  {error}
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
      ) : !hasSuccessfulUpdateReport ? (
        <PageStatus label="Update report missing..." icon="alert" color={Color.yellow} />
      ) : resolvedDetails?.report !== flowUpdateReport ? (
        <PageStatus label="Loading update report..." icon="spinner" />
      ) : (
        <Flex column gap="10px">
          <UpdateReportGroupedTable
            details={resolvedDetails.details}
            storePogIds={flowUpdateReport.storePogIds ?? {}}
            buildStorePogUrl={buildStorePogUrl}
          />
          {totalPages > 1 && (
            <Pagination
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              isLoading={isUpdateReportLoading}
            />
          )}
        </Flex>
      )}
    </Page>
  );
};
